import React, { useEffect, useState } from "react";
import { Checkbox, Modal, Skeleton, Tooltip, message } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { makeRequest, makeRequestStateless } from "@utils/helpers";

// Reusable org-entity manager used by Step 1. Lists existing records for one
// entity and lets the user: add (opens the entity's existing Create modal),
// edit (existing Update modal), and select/unselect (marks is_active — a checked
// item is active/included; unchecking soft-disables it through the entity's
// update request). Nothing is re-implemented — the real module modals do the work.
const EntitySection = (props) => {
  const {
    label, listReq, listKey, updateReq,
    CreateComp, UpdateComp,
    getExtraProps = () => ({}),
    onItemsChange,
  } = props;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [child, setChild] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const load = () => {
    makeRequest(
      setLoading,
      () => listReq({ page: 1, filters: "" }),
      null,
      (data) => {
        const arr = data?.[listKey]?.data || [];
        setItems(arr);
        if (onItemsChange) onItemsChange(arr);
      },
      () => {}
    );
  };

  // Called by the Create/Update modals: a real save passes the refreshed
  // records array, a cancel/close passes `false`. So only update the list on a
  // real save — never call the API just because the modal closed.
  const onCompleted = (data) => {
    setChild(null);
    if (Array.isArray(data) && data.length) {
      setItems(data);
      if (onItemsChange) onItemsChange(data);
    }
  };
  const noop = () => {};

  const openCreate = () => {
    setChild(
      <CreateComp
        onCompleted={onCompleted}
        onCreated={onCompleted}
        onSuccess={noop}
        {...getExtraProps(items)}
      />
    );
  };

  const openEdit = (record) => {
    setChild(
      <UpdateComp
        record={record}
        disabled={false}
        onCompleted={onCompleted}
        onCreated={onCompleted}
        {...getExtraProps(items)}
      />
    );
  };

  // select/unselect → persist is_active on the record (best-effort)
  const toggleActive = (record, checked) => {
    const applyChange = () => {
      setItems((list) => list.map((it) => (it.id === record.id ? { ...it, is_active: checked } : it)));
      if (!updateReq) return;
      makeRequestStateless(
        () => updateReq({ ...record, is_active: checked }),
        null,
        () => {},
        () => message.warning(`Could not update ${label.toLowerCase()} status — refresh to confirm current data.`)
      );
    };

    if (!checked) {
      Modal.confirm({
        title: `Disable ${record.name}?`,
        content: `This ${label.replace(/s$/, "").toLowerCase()} may already be used in employee records. Disable it only if HR has confirmed it is no longer needed.`,
        okText: "Disable",
        cancelText: "Keep active",
        onOk: applyChange,
      });
      return;
    }

    applyChange();
  };

  const isActive = (it) => it.is_active === undefined || it.is_active === null || !!it.is_active;

  return (
    <div className="onb-panel" style={{ marginBottom: 16 }}>
      <div className="onb-panel-h">
        <span>{label}</span>
        <span className="c">{items.length} item{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="onb-pills">
        <Skeleton active loading={loading} paragraph={{ rows: 1 }} title={false}>
          {items.map((it) => (
            <span
              key={it.id}
              className="onb-entity-pill"
              style={{ opacity: isActive(it) ? 1 : 0.5 }}
            >
              <Checkbox checked={isActive(it)} onChange={(e) => toggleActive(it, e.target.checked)} />
              <span className="nm">{it.name}</span>
              {it.code ? <span className="cd">{it.code}</span> : null}
              <Tooltip title={`Edit ${label.replace(/s$/, "").toLowerCase()}`}>
                <EditOutlined className="ed" onClick={() => openEdit(it)} />
              </Tooltip>
            </span>
          ))}
          <span className="onb-entity-pill add" onClick={openCreate}>
            <PlusOutlined /> Add new
          </span>
        </Skeleton>
      </div>
      {child}
    </div>
  );
};

export default EntitySection;
